import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createClient,
  getAllClient,
  getClientByID,
  ClientImageUpload,
  updateClient,
} from "./services/client.services";
import { clearClientDetail } from "./clientSlice";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { RxCross1 } from "react-icons/rx";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";

const CreateClient: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const ClientID = useAppSelector((state) => state.muidrawer.data);
  const ClientDetail = useAppSelector((state) => state.client.ClientDetail);
  const loading = useAppSelector((state) => state.client.formLoading);

  useEffect(() => {
    if (ClientID) {
      dispatch(getClientByID(ClientID));
    }
    return () => {
      dispatch(clearClientDetail());
    };
  }, [ClientID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      ClientName: values?.ClientName ? values?.ClientName : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const ClientLogo =
      values?.Logo &&
      typeof values?.Logo !== "string" &&
      Array.isArray(values?.Logo) &&
      values?.Logo?.length > 0
        ? values.Logo[0].getRawFile()
        : "";

    try {
      if (ClientID) {
        const editPayload = {
          ...formData,
          ID: ClientID,
        };

        const response = await dispatch(updateClient(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());

          if (
            values?.Logo &&
            typeof values?.Logo !== "string" &&
            Array.isArray(values?.Logo) &&
            values?.Logo?.length > 0
          ) {
            ImageData.append("Logo", ClientLogo);
            ImageData.append("ID", ClientID);
            await dispatch(ClientImageUpload(ImageData));
          }
          dispatch(getAllClient());
          dispatch(clearClientDetail());
        }
      } else {
        const response = await dispatch(createClient(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          if (
            values?.Logo &&
            typeof values?.Logo !== "string" &&
            Array.isArray(values?.Logo) &&
            values?.Logo?.length > 0 &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Logo", ClientLogo);

            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(ClientImageUpload(ImageData));
          }
          dispatch(getAllClient());
          dispatch(clearClientDetail());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <div>
        <Form
          onSubmit={handleSubmit}
          initialValues={ClientDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
                gap={{ rows: 10 }}
                cols={[{ width: "100%" }]}
              >
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {ClientID ? "Update Client" : "Create Client"}
                  </Typography.h4>
                  <Button
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                  >
                    <RxCross1 />
                  </Button>
                </GridLayoutItem>

                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="ClientName"
                    label="Client Name"
                    placeholder="i.e. Jhone Doe"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    justifyContent="start"
                    name={`Logo`}
                    label="Logo Image"
                    type="file"
                    accept="image/*"
                    alternateName="team-member-image"
                    component={FormImageUploadMorden}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={ClientID ? "Update" : "Create"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"secondary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateClient;
